
import React, { useEffect, useState, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import JSConfetti from 'js-confetti';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuthAndSubscription = async () => {
      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
        
        setIsAuthenticated(true);
        
        // If authenticated, check if they have an active subscription
        try {
          // First check if user is coming from a successful payment
          if (location.search.includes('payment=success')) {
            console.log('Payment success detected in URL');
            setHasActiveSubscription(true);
            setIsLoading(false);
            
            toast({
              title: "Payment successful!",
              description: "Thank you for your purchase. You now have full access to the dashboard.",
            });
            
            // Clear the URL parameters but preserve the state for confetti
            const currentState = location.state || {};
            window.history.replaceState(
              { ...currentState, fromLogin: true }, 
              document.title, 
              location.pathname
            );
            return;
          }
          
          console.log('Checking subscription for user:', session.user.id);
          
          // Check for any active subscription (will return an array instead of a single object)
          const { data: subscriptions, error: subsError } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('status', 'active');
            
          console.log('Active subscriptions found:', subscriptions?.length);
          
          // If we have at least one active subscription, user has access
          if (subscriptions && subscriptions.length > 0) {
            console.log('Found active subscription');
            setHasActiveSubscription(true);
            setIsLoading(false);
            return;
          }
          
          // If no active subscription, check one more time with a delay
          // This gives the webhook time to process if it hasn't yet
          if (location.pathname === '/dashboard') {
            toast({
              title: "Checking subscription status",
              description: "Please wait while we verify your subscription...",
            });
            
            // Wait 5 seconds and check again
            setTimeout(async () => {
              const { data: retrySubscriptions } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', session.user.id)
                .eq('status', 'active');
              
              console.log('Retry subscription check found:', retrySubscriptions?.length, 'subscriptions');
              setHasActiveSubscription(retrySubscriptions && retrySubscriptions.length > 0);
              setIsLoading(false);
            }, 5000);
            return;
          }
          
          setHasActiveSubscription(false);
          setIsLoading(false);
        } catch (subError) {
          console.error('Unexpected error checking subscription:', subError);
          setHasActiveSubscription(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error in auth check:', error);
        setIsLoading(false);
      }
    };
    
    checkAuthAndSubscription();
  }, [location.search, location.pathname, toast]);

  // Display confetti if coming from successful payment/login
  useEffect(() => {
    // Check if there's a "fromLogin" state in the location
    const isFromLogin = location.state && location.state.fromLogin === true;
    
    if (isFromLogin && !isLoading && hasActiveSubscription) {
      const jsConfetti = new JSConfetti();
      jsConfetti.addConfetti({
        emojis: ['🎵', '⚡️', '💥', '✨', '🎤', '🔊'],
        emojiSize: 30,
      });
      
      // Reset the state so confetti won't show on page refresh
      window.history.replaceState(
        {...location.state, fromLogin: false}, 
        document.title, 
        location.pathname
      );
    }
  }, [location.state, isLoading, hasActiveSubscription, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // If not authenticated, redirect to login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (!hasActiveSubscription) {
    // If authenticated but no active subscription, redirect to pricing
    return <Navigate to="/pricing" state={{ from: location }} replace />;
  }

  // If authenticated and has active subscription, render the children
  return <>{children}</>;
};

export default ProtectedRoute;
